/**
 * NewTaskDialog — Sprint 291 (Tasks V2)
 *
 * Modal creator that lives on `/tasks`. Writes admin tasks ONLY via
 * `addAdminTask` from TasksContext so the `type: 'task'` invariant cannot
 * be forgotten — this keeps operational field-work surfaces (/dispatch,
 * /jobs, /today, /calendar) uncontaminated per the iter 146 dual-view
 * contract.
 *
 * Client/lead association is *optional* — a task like
 *   "Call supplier about EcoVia inventory"
 * is a legitimate first-class citizen with no leadId.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'sonner';
import { Plus, Search, X, User as UserIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import { useCompany } from '../../context/CompanyContext';
import { useAuth } from '../../context/AuthContext';
import { getDisplayName } from '../../lib/utils';

const TASK_TYPES = [
  { value: 'call',      label: 'Call' },
  { value: 'email',     label: 'Email' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'other',     label: 'Other' },
];

const PRIORITIES = [
  { value: 'low',    label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High' },
];

export default function NewTaskDialog({ open, onOpenChange, defaultAssigneeUid }) {
  const { addAdminTask } = useTasks();
  const { leads }        = useLeads();
  const { companyId }    = useCompany();
  const { user }         = useAuth();

  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [taskType, setTaskType]       = useState('call');
  const [priority, setPriority]       = useState('medium');
  const [dueDate, setDueDate]         = useState('');
  const [dueTime, setDueTime]         = useState('');
  const [leadId, setLeadId]           = useState('');       // '' = no client
  const [leadQuery, setLeadQuery]     = useState('');
  const [assignedTo, setAssignedTo]   = useState('');       // '' = Unassigned
  const [companyUsers, setCompanyUsers] = useState([]);
  const [submitting, setSubmitting]   = useState(false);

  // Load team members on open (once per open)
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const membersSnap = await getDocs(
          collection(db, 'companies', companyId, 'users')
        );
        const members = membersSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));
        const enriched = await Promise.all(members.map(async (m) => {
          let profile = {};
          try {
            const s = await getDoc(doc(db, 'users', m.uid));
            if (s.exists()) profile = s.data();
          } catch { /* self-read only per rules */ }
          const email = profile.email || m.email || '';
          const displayName =
            profile.displayName || profile.name || m.name ||
            (email ? email.split('@')[0] : m.uid);
          return { uid: m.uid, displayName, email, role: m.role };
        }));
        if (!cancelled) setCompanyUsers(enriched);
      } catch (err) {
        console.error('[NewTaskDialog] Failed to load team:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, companyId]);

  // Reset when dialog closes
  useEffect(() => {
    if (open) {
      setAssignedTo(defaultAssigneeUid || user?.uid || '');
      return;
    }
    setTitle('');
    setDescription('');
    setTaskType('call');
    setPriority('medium');
    setDueDate('');
    setDueTime('');
    setLeadId('');
    setLeadQuery('');
    setAssignedTo('');
    setSubmitting(false);
  }, [open, defaultAssigneeUid, user?.uid]);

  const filteredLeads = useMemo(() => {
    const q = leadQuery.trim().toLowerCase();
    if (!q) return leads.slice(0, 8);
    return leads
      .filter((l) => getDisplayName(l).toLowerCase().includes(q))
      .slice(0, 8);
  }, [leads, leadQuery]);

  const chosenLead = leadId ? leads.find((l) => l.id === leadId) : null;

  const canSubmit = title.trim().length > 0 && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Resolve display name for assignee (for the assignedToName mirror on the doc).
      const assignee = assignedTo
        ? companyUsers.find((u) => u.uid === assignedTo)
        : null;

      const payload = {
        title:        title.trim(),
        description:  description.trim() || '',
        taskType,
        priority,
        status:       'pending',
        dueDate:      dueDate || null,
        dueTime:      dueTime || null,
        leadId:       leadId || null,
        assignedTo:   assignedTo || null,
        assignedToName: assignee ? assignee.displayName : null,
      };

      await addAdminTask(payload);
      toast.success('Task created');
      onOpenChange(false);
    } catch (err) {
      console.error('[NewTaskDialog] Create failed:', err);
      toast.error('Could not create task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid="new-task-dialog"
      >
        <DialogHeader>
          <DialogTitle>New Task</DialogTitle>
          <DialogDescription>
            Callbacks, follow-ups, and internal workflow items. For a customer
            visit, use Schedule Job or Dispatch instead.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {/* Title (required) */}
          <div className="space-y-1.5">
            <Label htmlFor="new-task-title" className="text-xs font-medium">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="new-task-title"
              data-testid="new-task-title-input"
              placeholder="e.g. Call supplier about EcoVia inventory"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="new-task-desc" className="text-xs font-medium">
              Description
            </Label>
            <Textarea
              id="new-task-desc"
              data-testid="new-task-desc-input"
              placeholder="Optional details"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          {/* Client (optional) */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              Client <span className="text-gray-400">(optional)</span>
            </Label>
            {chosenLead ? (
              <div
                className="flex items-center justify-between gap-2 px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
                data-testid="new-task-selected-lead"
              >
                <span className="text-sm text-gray-800 truncate">
                  {getDisplayName(chosenLead)}
                </span>
                <button
                  type="button"
                  onClick={() => { setLeadId(''); setLeadQuery(''); }}
                  className="p-1 rounded hover:bg-gray-200"
                  aria-label="Remove client"
                  data-testid="new-task-clear-lead"
                >
                  <X className="w-3.5 h-3.5 text-gray-500" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    data-testid="new-task-lead-search"
                    placeholder="Search a client or leave blank for internal task"
                    value={leadQuery}
                    onChange={(e) => setLeadQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {leadQuery && (
                  <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto bg-white">
                    {filteredLeads.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-gray-400">
                        No matches
                      </div>
                    ) : filteredLeads.map((l) => (
                      <button
                        type="button"
                        key={l.id}
                        onClick={() => { setLeadId(l.id); setLeadQuery(''); }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        data-testid={`new-task-lead-option-${l.id}`}
                      >
                        {getDisplayName(l)}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Assignee */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Assignee</Label>
            <Select value={assignedTo || '__none__'} onValueChange={(v) => setAssignedTo(v === '__none__' ? '' : v)}>
              <SelectTrigger data-testid="new-task-assignee">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {user?.uid && (
                  <SelectItem value={user.uid}>
                    Me
                  </SelectItem>
                )}
                {companyUsers
                  .filter((u) => u.uid !== user?.uid)
                  .map((u) => (
                    <SelectItem key={u.uid} value={u.uid}>
                      {u.displayName}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Type + priority */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Type</Label>
              <Select value={taskType} onValueChange={setTaskType}>
                <SelectTrigger data-testid="new-task-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="new-task-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Due date + time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Due date</Label>
              <Input
                type="date"
                data-testid="new-task-due-date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Due time</Label>
              <Input
                type="time"
                data-testid="new-task-due-time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="new-task-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="new-task-submit"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create Task
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
